import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, addMonths, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  RefreshCw, 
  ChevronLeft, 
  ChevronRight, 
  FileText, 
  AlertCircle,
  Info,
  CheckCircle,
  ArrowLeft,
  MoreVertical,
  Search,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { usePurchaseInvoices } from '@/hooks/usePurchaseInvoices';
import { useDebounce } from '@/hooks/useDebounce';
import { PurchaseInvoicesList } from '@/components/Purchases/PurchaseInvoicesList';
import { PurchaseInvoiceWizard } from '@/components/Purchases/PurchaseInvoiceWizard';
import { LinkInvoiceDialog } from '@/components/Purchases/LinkInvoiceDialog';
import { FiscalDiagnosticDialog } from '@/components/Purchases/FiscalDiagnosticDialog';
import type { PendingPurchaseInvoice } from '@/types/purchaseInvoice';

export default function PurchaseInvoicesPage() {
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState(new Date());

  // Log page mount for debugging
  useEffect(() => {
    console.log('[PurchaseInvoicesPage] Página montada', {
      selectedMonth: selectedMonth.toISOString(),
      pathname: window.location.pathname,
      timestamp: new Date().toISOString(),
    });
  }, []);
  const [selectedInvoice, setSelectedInvoice] = useState<PendingPurchaseInvoice | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [diagnosticOpen, setDiagnosticOpen] = useState(false);
  const [diagnosticData, setDiagnosticData] = useState<any>(null);
  // R-NF.7: busca por chave/número/fornecedor/CNPJ — debounce no molde useCustomerSearch
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 300);

  const {
    pendingInvoices,
    lastSync,
    isLoading,
    isSyncing,
    syncInvoices,
    checkFiscalStatus,
    isCheckingFiscalStatus,
    buscaAtiva,
  } = usePurchaseInvoices(selectedMonth, debouncedSearch);

  const handlePreviousMonth = () => {
    setSelectedMonth(prev => subMonths(prev, 1));
  };

  const handleNextMonth = () => {
    setSelectedMonth(prev => addMonths(prev, 1));
  };

  const handleSync = () => {
    syncInvoices();
  };

  const handleOpenDiagnostic = async () => {
    setDiagnosticOpen(true);
    setDiagnosticData(null);
    try {
      const data = await checkFiscalStatus();
      setDiagnosticData(data);
    } catch (error) {
      console.error('Error checking fiscal status:', error);
      setDiagnosticData({ success: false, error: (error as Error).message });
    }
  };

  const handleRefreshDiagnostic = async () => {
    setDiagnosticData(null);
    try {
      const data = await checkFiscalStatus();
      setDiagnosticData(data);
    } catch (error) {
      console.error('Error checking fiscal status:', error);
      setDiagnosticData({ success: false, error: (error as Error).message });
    }
  };

  const handleGenerateClick = (invoice: PendingPurchaseInvoice) => {
    setSelectedInvoice(invoice);
    setWizardOpen(true);
  };

  const handleLinkClick = (invoice: PendingPurchaseInvoice) => {
    setSelectedInvoice(invoice);
    setLinkDialogOpen(true);
  };

  const handleWizardClose = () => {
    setWizardOpen(false);
    setSelectedInvoice(null);
  };

  const handleLinkDialogClose = () => {
    setLinkDialogOpen(false);
    setSelectedInvoice(null);
  };

  // Calculate stats
  const stats = {
    total: pendingInvoices.length,
    pending: pendingInvoices.filter(i => i.status === 'pending').length,
    processed: pendingInvoices.filter(i => i.status === 'processed').length,
    linked: pendingInvoices.filter(i => i.status === 'linked').length,
    cancelled: pendingInvoices.filter(i => i.sefaz_status === 'cancelada' || i.sefaz_status === 'denegada').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard/purchases')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Notas Fiscais de Entrada</h1>
            <p className="text-muted-foreground">
              Gerencie as notas fiscais emitidas contra seu CNPJ
            </p>
          </div>
        </div>
      </div>

      {/* Sync Banner */}
      <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-900">
        <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        <AlertDescription className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-blue-800 dark:text-blue-200">
              Busca automática de NF-e na SEFAZ via Manifestação do Destinatário
            </span>
            {lastSync?.last_sync_at && (
              <span className="text-xs text-blue-600 dark:text-blue-400">
                • Última sincronização: {format(new Date(lastSync.last_sync_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleSync}
              disabled={isSyncing}
              className="border-blue-300 hover:bg-blue-100 dark:border-blue-700 dark:hover:bg-blue-900"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
              {isSyncing ? 'Sincronizando...' : 'Atualizar lista'}
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" disabled={isSyncing}>
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleOpenDiagnostic}>
                  <Search className="h-4 w-4 mr-2" />
                  Diagnóstico Fiscal
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </AlertDescription>
      </Alert>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total do Mês</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pendentes</CardTitle>
            <AlertCircle className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Processadas</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.processed}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Vinculadas</CardTitle>
            <FileText className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats.linked}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Canceladas</CardTitle>
            <AlertCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats.cancelled}</div>
          </CardContent>
        </Card>
      </div>

      {/* Month Navigator */}
      <div className="flex items-center justify-center gap-4">
        <Button variant="outline" size="icon" onClick={handlePreviousMonth}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-lg font-medium min-w-[200px] text-center">
          {format(selectedMonth, "MMMM 'de' yyyy", { locale: ptBR })}
          {buscaAtiva && (
            <span className="block text-xs font-normal text-muted-foreground">busca em todos os meses</span>
          )}
        </span>
        <Button variant="outline" size="icon" onClick={handleNextMonth}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* R-NF.7: busca server-side (mín. 4 caracteres suspende o filtro de mês) */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Chave, número, fornecedor ou CNPJ (mín. 4 caracteres)"
          className="pl-9"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Invoices List */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : (
        <PurchaseInvoicesList
          invoices={pendingInvoices}
          onGenerateClick={handleGenerateClick}
          onLinkClick={handleLinkClick}
        />
      )}

      {/* Import Wizard Dialog */}
      {selectedInvoice && wizardOpen && (
        <PurchaseInvoiceWizard
          invoice={selectedInvoice}
          open={wizardOpen}
          onClose={handleWizardClose}
        />
      )}

      {/* Link Invoice Dialog */}
      {selectedInvoice && linkDialogOpen && (
        <LinkInvoiceDialog
          invoice={selectedInvoice}
          open={linkDialogOpen}
          onClose={handleLinkDialogClose}
        />
      )}

      {/* Fiscal Diagnostic Dialog */}
      <FiscalDiagnosticDialog
        open={diagnosticOpen}
        onOpenChange={setDiagnosticOpen}
        diagnostic={diagnosticData}
        isLoading={isCheckingFiscalStatus}
        onRefresh={handleRefreshDiagnostic}
      />
    </div>
  );
}
